import React, { useEffect } from 'react';
import TopNavigation from './TopNavigation';
import ClueRaiderBanner from './components/ClueRaiderBanner';
import book0 from './book0.png';
import book1 from './book1.png';
import book2 from './book2.png';
import book3 from './book3.png';
import amazonLogo from './amazon.png';
import { analytics } from './services/analytics';
import { updatePageSEO } from './utils/seo';

const AMAZON_URL = 'https://mybook.to/dimensionflux';

const featureBullets = [
  '50 brand-new Dimension Flux logic puzzles exclusive to the print edition.',
  'Play totally offline—no timers, no ads, just deduction at your own pace.',
  'Daily-style progression in a curated order from Easy to Expert.',
  'Large-format case spreads with greyscale art, running headers, and space for working notes.',
  'Flux Directorate briefing front matter to introduce new agents to containment protocol.',
];

const imageShowcase = [
  { src: book1, alt: 'Sample case spread showing headline and incident report', caption: 'Case #3 Incident report and artwork.' },
  { src: book2, alt: 'Interior page showing clues and suspects tables', caption: 'Case #3 Clues & Suspects layout with clean tables for note-taking.' },
  { src: book3, alt: 'Solutions section preview', caption: 'Solutions archive with verified triads and briefing notes.' },
];

const Book: React.FC = () => {
  useEffect(() => {
    analytics.trackBookPageView();
  }, []);

  // Ensure page opens at top when navigating here
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  // Update SEO
  useEffect(() => {
    updatePageSEO(
      'Dimension Flux: 50 Inter-Dimensional Logic Puzzles - Print Edition',
      'A field dossier you can drop on the table, pack in a bag, or gift to fellow agents—fifty brand-new logic cases available only in print.'
    );
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <TopNavigation />
      <ClueRaiderBanner />

      <main className="max-w-xl mx-auto w-full px-3 sm:px-4 pt-4 pb-8">
        <section className="bg-white bg-opacity-95 border-2 border-gray-300 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-4 sm:px-6 py-6 text-gray-900">
            <h1 className="text-2xl sm:text-3xl font-oswald font-bold text-center leading-tight mb-3">
              Dimension Flux:<br />50 Inter-Dimensional Logic Puzzles
            </h1>
            <p className="text-center text-base font-lora text-gray-700">
              A field dossier you can drop on the table, pack in a bag, or gift to fellow agents—fifty brand-new logic
              cases available only in print.
            </p>
          </div>

          <div className="bg-white bg-opacity-95 border-t border-gray-200 overflow-hidden">
            <img src={book0} alt="Book cover of Dimension Flux print edition" className="w-full object-contain bg-gray-100" />
          </div>

          <div className="border-t border-gray-200 px-4 sm:px-6 py-5 bg-gray-50 text-gray-800">
            <h2 className="text-lg font-oswald font-semibold text-gray-900 mb-3">
              What&apos;s Inside
            </h2>
            <ul className="space-y-2 text-sm sm:text-base font-lora leading-relaxed">
              {featureBullets.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-slate-700" aria-hidden="true"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 flex justify-center">
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex"
              onClick={() => analytics.trackBookAmazonClick('details')}
            >
              <img
                src={amazonLogo}
                alt="Available at Amazon"
                className="h-24 sm:h-28 w-auto object-contain"
              />
            </a>
          </div>
        </section>

        <section className="mt-6 space-y-5">
          {imageShowcase.map(({ src, alt, caption }) => (
            <figure
              key={alt}
              className="bg-white bg-opacity-95 border-2 border-gray-300 rounded-2xl shadow-xl overflow-hidden"
            >
              <img src={src} alt={alt} className="w-full object-contain bg-gray-100" />
              <figcaption className="px-4 sm:px-6 py-3 text-sm font-lora text-gray-700 bg-white bg-opacity-95">
                {caption}
              </figcaption>
            </figure>
          ))}
        </section>

        <section className="mt-6 bg-white bg-opacity-95 border-2 border-gray-300 rounded-2xl shadow-xl px-4 sm:px-6 py-5 text-gray-900">
          <h2 className="text-lg font-oswald font-semibold text-gray-900 mb-3">
            Flux Directorate Briefing
          </h2>
          <p className="font-lora text-sm sm:text-base leading-relaxed text-gray-700">
            “Every universe leaks eventually. The trick is finding the mop before the flood.” Reality is a fragile weave
            of probabilities. When it tears, impossible things appear—objects that shouldn’t exist, creatures from
            different histories, cities that hum with alien energy. The Flux Directorate calls these events Breaches. Your
            task is to deduce the only combination of Being, Weapon, and Hideout that explains each disturbance and close
            the rift before reality unravels.
          </p>
          <p className="font-lora text-sm sm:text-base leading-relaxed text-gray-700 mt-3">
            No guessing. No luck. Just logic… and the flux. Bring a pencil, gather your instincts, and take these cases on
            the road.
          </p>
          <div className="mt-5 text-center">
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-slate-800 hover:bg-slate-900 text-white px-6 py-3 rounded-xl font-oswald font-semibold tracking-wide shadow-lg transition-colors duration-200"
            >
              Get the Print Edition
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-white">
          <p>© N.M.A. Pitman 2025 • Contact: <a href="mailto:hello@dimensionflux.com" className="underline">hello@dimensionflux.com</a></p>
          <p className="mt-1 text-xs opacity-80"><a href="https://www.vecteezy.com/free-vector/portal">Portal Vectors by Vecteezy</a></p>
        </footer>
      </main>
    </div>
  );
};

export default Book;


